import { useEffect, useMemo, useState } from "react"
import { useSelector } from "react-redux"
import { useTranslation } from "react-i18next"

import ModalService from "src/modals"
import Modal from "src/components/modals/Modal"
import { selectGameConfig } from "src/store/common/selectors"
import { GRAPHS, seriesFrom } from "src/utils/gameGraphs"
import "./GameGraphsModal.css"

/* The end-of-game panels (owner, 2026-09-22): production, consumption, research and kills, one
   line per player against the round.

   **Two hues, not one per player.** The colour is the TEAM's — the same green and red the map
   paints the two sides with — and teammates are told apart by the line's dash and by the name
   written at the end of its own line. Six distinguishable hues do not exist on this surface: six
   candidates measured a colourblind separation of 2.6 against a floor of 8, where the two team
   colours measure 16.2 and 35.7 to normal vision. Identity is never colour alone here anyway, so
   the dash carries the third player and the hue keeps meaning what it means everywhere else.

   **Four charts, never one with two axes.** Food is in bodies, research in points and kills in
   bodies killed; they do not share a scale and a second y axis is the one chart mistake this
   codebase should not make. Each panel is scaled to its own maximum, which is written on it.

   Hovering anywhere gives the round under the pointer on every line at once, which is the question
   a post-game graph is actually asked ("what happened on round twelve?"). */

// Two panels a row, so the viewBox is squarer than a full-width strip would be, and the padding
// is real: the labels at the right and the ticks at the left need room that a chart drawn edge to
// edge does not give them (owner, 2026-09-22 — "no margins and padding, extremely hard to read").
const W = 460
const H = 190
const PAD = { left: 56, right: 72, top: 16, bottom: 34 }
const DASHES = ["", "7 4", "2 4", "11 4", "1 3", "5 3 1 3"]

const niceTop = (value) => {
  if (value <= 0) return 1
  const magnitude = 10 ** Math.floor(Math.log10(value))
  return Math.ceil(value / magnitude) * magnitude
}

/* Two lines that end on the same value put their names in the same place — which happens every
   time a chart finishes at zero, and the research panel finishes at zero in most games. Nudge the
   labels apart along y, keeping their order, so a collision reads as two names rather than one
   smear. */
const spread = (entries, gap = 11) => {
  const sorted = [...entries].sort((a, b) => a.y - b.y)
  sorted.forEach((entry, index) => {
    if (index === 0) return
    const above = sorted[index - 1]
    if (entry.y - above.y < gap) entry.y = above.y + gap
  })
  return sorted
}

const Chart = ({ title, unit, ticks, lines, hovered, onHover }) => {
  const top = niceTop(Math.max(0, ...lines.flatMap((line) => line.values)))
  const span = Math.max(1, ticks.length - 1)
  const x = (index) => PAD.left + (index / span) * (W - PAD.left - PAD.right)
  const y = (value) => H - PAD.bottom - (value / top) * (H - PAD.top - PAD.bottom)
  const plotted = lines.filter((line) => line.values.length)

  return (
    <figure className="graph">
      <figcaption>
        {title} <span className="graph-unit">{unit}</span>
      </figcaption>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        role="img"
        aria-label={title}
        onMouseLeave={() => onHover(null)}
        onMouseMove={(event) => {
          const box = event.currentTarget.getBoundingClientRect()
          const at = ((event.clientX - box.left) / box.width) * W
          const index = Math.round(((at - PAD.left) / (W - PAD.left - PAD.right)) * span)
          onHover(Math.min(ticks.length - 1, Math.max(0, index)))
        }}
      >
        {[0, 0.5, 1].map((fraction) => (
          <g key={fraction}>
            <line
              className="graph-grid"
              x1={PAD.left}
              x2={W - PAD.right}
              y1={y(top * fraction)}
              y2={y(top * fraction)}
            />
            <text className="graph-tick" x={PAD.left - 6} y={y(top * fraction) + 3} textAnchor="end">
              {Math.round(top * fraction).toLocaleString()}
            </text>
          </g>
        ))}
        <text className="graph-tick" x={PAD.left} y={H - 6}>
          {ticks[0] ?? 0}
        </text>
        <text className="graph-tick" x={W - PAD.right} y={H - 6} textAnchor="end">
          {ticks[ticks.length - 1] ?? 0}
        </text>

        {hovered != null && (
          <line className="graph-crosshair" x1={x(hovered)} x2={x(hovered)} y1={PAD.top} y2={H - PAD.bottom} />
        )}

        {plotted.map((line) => (
          <path
            key={line.id}
            className={`graph-line graph-${line.team}`}
            strokeDasharray={line.dash || undefined}
            d={line.values.map((value, index) => `${index ? "L" : "M"}${x(index)} ${y(value)}`).join(" ")}
          />
        ))}

        {/* The value the pointer is on, written on the line rather than in a floating box: four
            panels share one crosshair, so four tooltips would be four boxes chasing the cursor. */}
        {hovered != null &&
          spread(plotted.map((line) => ({ line, y: y(line.values[hovered] || 0) + 3 }))).map(({ line, y: at }) => (
            <g key={`${line.id}-read`}>
              <circle className={`graph-dot graph-${line.team}`} cx={x(hovered)} cy={y(line.values[hovered] || 0)} r={4} />
              <text className="graph-readout" x={W - PAD.right + 6} y={at}>
                {(line.values[hovered] || 0).toLocaleString()}
              </text>
            </g>
          ))}

        {/* Direct labels: the name at the end of its own line, so identity is never colour alone. */}
        {hovered == null &&
          spread(
            plotted.map((line) => ({ line, y: y(line.values[line.values.length - 1] || 0) + 3 }))
          ).map(({ line, y: at }) => (
            <text
              key={`${line.id}-label`}
              className={`graph-label graph-${line.team}`}
              x={W - PAD.right + 6}
              y={at}
            >
              {line.name}
            </text>
          ))}
      </svg>
    </figure>
  )
}

const GameGraphsModal = ({ steps, players }) => {
  const { t } = useTranslation()
  const gameConfig = useSelector(selectGameConfig)
  const [hovered, setHovered] = useState(null)
  const [asTable, setAsTable] = useState(false)

  const { ticks, seats, hasNumbers } = useMemo(
    () => seriesFrom({ steps, players, gameConfig }),
    [steps, players, gameConfig]
  )

  // A dash per player WITHIN a team, so the two sides keep their own colour and teammates are
  // still told apart.
  const perTeam = {}
  const lines = seats.map((seat) => {
    perTeam[seat.team] = (perTeam[seat.team] || 0) + 1
    return { ...seat, dash: DASHES[(perTeam[seat.team] - 1) % DASHES.length] }
  })

  useEffect(() => {
    if (!hasNumbers) setAsTable(false)
  }, [hasNumbers])

  return (
    <Modal width="78%" top="6vh" maxHeight="90vh">
      <h2 className="graph-title">{t("graphs.title")}</h2>
      {!hasNumbers ? (
        <p className="p-small graph-empty">{t("graphs.none")}</p>
      ) : (
        <>
          <div className="graph-legend">
            {lines.map((line) => (
              <span key={line.id} className="graph-legend-item">
                <svg width="26" height="8" aria-hidden="true">
                  <line
                    className={`graph-line graph-${line.team}`}
                    strokeDasharray={line.dash || undefined}
                    x1="1"
                    x2="25"
                    y1="4"
                    y2="4"
                  />
                </svg>
                {line.name}
              </span>
            ))}
            <button type="button" className="graph-toggle" onClick={() => setAsTable((on) => !on)}>
              {asTable ? t("graphs.asChart") : t("graphs.asNumbers")}
            </button>
          </div>

          {asTable ? (
            <div className="graph-table-wrap">
              <table className="graph-table">
                <thead>
                  <tr>
                    <th>{t("graphs.round")}</th>
                    {GRAPHS.flatMap((graph) =>
                      lines.map((line) => (
                        <th key={`${graph.key}-${line.id}`}>
                          {t(graph.titleKey)} · {line.name}
                        </th>
                      ))
                    )}
                  </tr>
                </thead>
                <tbody>
                  {ticks.map((tick, index) => (
                    <tr key={tick}>
                      <td>{tick}</td>
                      {GRAPHS.flatMap((graph) =>
                        lines.map((line) => (
                          <td key={`${graph.key}-${line.id}`}>
                            {(line.series[graph.key][index] || 0).toLocaleString()}
                          </td>
                        ))
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="graph-grid">
              {GRAPHS.map((graph) => (
                <Chart
                  key={graph.key}
                  title={t(graph.titleKey)}
                  unit={t(graph.unitKey)}
                  ticks={ticks}
                  hovered={hovered}
                  onHover={setHovered}
                  lines={lines.map((line) => ({ ...line, values: line.series[graph.key] }))}
                />
              ))}
            </div>
          )}
          {hovered != null && (
            <p className="p-mini graph-hovered">{t("graphs.round")} {ticks[hovered]}</p>
          )}
        </>
      )}
      <button type="button" className="btn" onClick={() => ModalService.show("GameOverModal")}>
        {t("graphs.back")}
      </button>
    </Modal>
  )
}

export default GameGraphsModal
